import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

export function useProductExtraction(locationId, country) {
  const { getAccessTokenSilently } = useAuth0();
  const [files, setFiles] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedProducts, setExtractedProducts] = useState([]);
  const [error, setError] = useState(null);

  const addFiles = useCallback((newFiles) => {
    const fileEntries = Array.from(newFiles).map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      status: 'ready',
    }));
    setFiles(prev => [...prev, ...fileEntries]);
  }, []);

  const removeFile = useCallback((fileId) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== fileId);
    });
  }, []);

  const clearFiles = useCallback(() => {
    files.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setFiles([]);
    setExtractedProducts([]);
    setError(null);
  }, [files]);

  const extractProducts = useCallback(async () => {
    if (files.length === 0) return;

    setIsExtracting(true);
    setError(null);
    setFiles(prev => prev.map(f => ({ ...f, status: 'uploading' })));

    try {
      const token = await getAccessTokenSilently();
      const formData = new FormData();
      files.forEach(f => formData.append('files', f.file));
      if (locationId) formData.append('location_id', locationId);
      if (country) formData.append('country', country);

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/flyer-analysis/extract-products`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        }
      );

      const products = response.data.products || response.data || [];

      // Process match results into statuses:
      //   'resolved'      — matched to local catalog (green)
      //   'pick'          — multiple local candidates, user picks (amber)
      //   'global'        — matched to global library, can add to catalog (blue)
      //   'global_pick'   — multiple global candidates, user picks (blue)
      //   'unmatched'     — no match anywhere (gray)
      const processed = products.map(p => {
        const localCandidates = p.candidates || [];
        const globalCandidates = p.global_candidates || [];

        // Case 1: Server auto-resolved to local catalog
        if (p.matched && p.matchSource === 'local') {
          return { ...p, _status: 'resolved' };
        }
        // Case 2: Server auto-resolved to global library
        if (p.matched && (p.matchSource === 'universal' || p.matchSource === 'master')) {
          return { ...p, _status: 'global' };
        }
        // Case 3: Multiple local candidates
        if (localCandidates.length > 1) {
          return { ...p, _status: 'pick' };
        }
        // Case 4: Single local candidate — auto-resolve
        if (localCandidates.length === 1) {
          const match = localCandidates[0];
          return {
            ...p,
            ...mergeMatchedProduct(p, match),
            matched: true,
            matchSource: 'local',
            matched_product: match,
            candidates: [],
            _status: 'resolved',
          };
        }
        // Case 5: Multiple global candidates
        if (globalCandidates.length > 1) {
          return { ...p, _status: 'global_pick' };
        }
        // Case 6: Single global candidate — auto-resolve as global
        if (globalCandidates.length === 1) {
          const match = globalCandidates[0];
          return {
            ...p,
            ...mergeGlobalProduct(p, match),
            matched: true,
            matchSource: match._source || 'universal',
            global_product: match,
            global_candidates: [],
            _status: 'global',
          };
        }
        // Case 7: No match anywhere
        return { ...p, _status: 'unmatched' };
      });

      setExtractedProducts(processed);
      setFiles(prev => prev.map(f => ({ ...f, status: 'done' })));
    } catch (err) {
      console.error('Product extraction failed:', err);
      setError(err.response?.data?.message || 'Failed to extract products. Please try again.');
      setFiles(prev => prev.map(f => ({ ...f, status: 'error' })));
    } finally {
      setIsExtracting(false);
    }
  }, [files, getAccessTokenSilently, locationId, country]);

  // User picks a local catalog candidate
  const resolveProduct = useCallback((productId, chosenProduct) => {
    setExtractedProducts(prev => prev.map(p => {
      if ((p.id || p.name) !== productId) return p;
      return {
        ...p,
        ...mergeMatchedProduct(p, chosenProduct),
        matched: true,
        matchSource: 'local',
        matched_product: chosenProduct,
        candidates: [],
        _status: 'resolved',
        confidence: 1.0,
      };
    }));
  }, []);

  // User picks a global library candidate
  const resolveGlobalProduct = useCallback((productId, chosenProduct) => {
    setExtractedProducts(prev => prev.map(p => {
      if ((p.id || p.name) !== productId) return p;
      return {
        ...p,
        ...mergeGlobalProduct(p, chosenProduct),
        matched: true,
        matchSource: chosenProduct._source || 'universal',
        global_product: chosenProduct,
        global_candidates: [],
        _status: 'global',
        confidence: 1.0,
      };
    }));
  }, []);

  // User dismisses candidates — keep extracted data as-is
  const dismissCandidates = useCallback((productId) => {
    setExtractedProducts(prev => prev.map(p => {
      if ((p.id || p.name) !== productId) return p;
      return { ...p, candidates: [], global_candidates: [], _status: 'unmatched' };
    }));
  }, []);

  // Add a global library product to the retailer's local catalog
  const addToCatalog = useCallback(async (productId) => {
    const product = extractedProducts.find(p => (p.id || p.name) === productId);
    if (!product || !product.global_product) return;

    const gp = product.global_product;
    const source = gp._source === 'master' ? 'master' : 'universal';
    const globalProductId = product.universal_product_id || product.master_product_id || gp._id;

    try {
      const token = await getAccessTokenSilently();
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/flyer-analysis/add-to-catalog`,
        {
          location_id: locationId,
          source,
          global_product_id: globalProductId,
          price: product.regular_price || product.original_price,
          promo_price: product.sale_price || product.promo_price,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const createdProduct = response.data.product;

      // Update the extracted product to reflect it's now in the local catalog
      setExtractedProducts(prev => prev.map(p => {
        if ((p.id || p.name) !== productId) return p;
        return {
          ...p,
          ...mergeMatchedProduct(p, createdProduct),
          matched: true,
          matchSource: 'local',
          matched_product: createdProduct,
          _status: 'resolved',
          _addedToCatalog: true,
        };
      }));

      return createdProduct;
    } catch (err) {
      console.error('Add to catalog failed:', err);
      throw err;
    }
  }, [extractedProducts, getAccessTokenSilently, locationId]);

  const clearExtracted = useCallback(() => {
    setExtractedProducts([]);
  }, []);

  return {
    files,
    addFiles,
    removeFile,
    clearFiles,
    isExtracting,
    extractProducts,
    extractedProducts,
    clearExtracted,
    resolveProduct,
    resolveGlobalProduct,
    dismissCandidates,
    addToCatalog,
    error,
  };
}

// Merge matched local catalog product fields onto extracted product
function mergeMatchedProduct(extracted, match) {
  return {
    retailer_sku_id: match.retailer_sku_id,
    product_name: match.product_name,
    product_description: match.product_description,
    brand: match.brand || extracted.brand,
    category: match.category || extracted.category,
    promo_price: extracted.sale_price || extracted.promo_price || match.promo_price,
    original_price: extracted.regular_price || extracted.original_price || match.original_price,
    promo_type: match.promo_type,
    price_unit: match.price_unit || extracted.price_unit,
    variant_id: match.variant_id,
    limit_qty: match.limit_qty,
    cleaned_img_large_src: match.custom_image_url || match.original_img_large_src || match.img_large_src || match.image_url,
    original_img_large_src: match.original_img_large_src || match.img_large_src || match.image_url,
    cleaned_img_small_src: match.img_small_src,
    image_url: match.image_url,
  };
}

// Merge global library product fields — prices come from photo extraction
function mergeGlobalProduct(extracted, globalProduct) {
  const imgUrl = globalProduct.image_url || globalProduct.primaryImageUrl || globalProduct.cleaned_product_image_url;
  return {
    product_name: globalProduct.product_name || globalProduct.name || extracted.name,
    brand: globalProduct.brand || extracted.brand,
    category: globalProduct.category || extracted.category,
    promo_price: extracted.sale_price || extracted.promo_price,
    original_price: extracted.regular_price || extracted.original_price,
    price_unit: extracted.price_unit,
    image_url: imgUrl,
    cleaned_img_large_src: imgUrl,
    original_img_large_src: imgUrl,
    universal_product_id: globalProduct._source === 'universal' ? globalProduct._id : undefined,
    master_product_id: globalProduct._source === 'master' ? globalProduct._id : undefined,
    pack_size: globalProduct.package_size || globalProduct.size || extracted.quantity,
  };
}
